import { useEffect, useMemo, useState } from 'react';
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type RowSelectionState,
  type SortingState,
  type VisibilityState,
} from '@tanstack/react-table';
import {
  ArrowDown,
  ArrowUp,
  ChevronsUpDown,
  Download,
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Inbox,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { t } from '@/locales/i18n';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';

export type Density = 'compact' | 'normal' | 'comfortable';

export interface DataTableProps<TData, TValue> {
  data: TData[];
  columns: ColumnDef<TData, TValue>[];
  loading?: boolean;
  emptyMessage?: string;
  searchPlaceholder?: string;
  pageSize?: number;
  enableSelection?: boolean;
  exportFileName?: string;
  className?: string;
  /** Callback fired with the rows currently selected (after every selection change). */
  onSelectionChange?: (rows: TData[]) => void;
  /** Used to identify a row uniquely (default: index). Useful so selection survives data refresh. */
  getRowId?: (row: TData) => string;
  /** Slot rendered above the toolbar (typically a BulkActionBar). */
  bulkBar?: React.ReactNode;
}

const DENSITY_PADDING: Record<Density, string> = {
  compact: 'h-9 px-3 py-1.5 text-xs',
  normal: 'h-10 px-3 py-2 text-sm',
  comfortable: 'h-11 px-3 py-2.5 text-sm',
};

export function DataTable<TData, TValue>({
  data,
  columns,
  loading = false,
  emptyMessage,
  searchPlaceholder,
  pageSize = 10,
  enableSelection = false,
  exportFileName = 'export.csv',
  className,
  onSelectionChange,
  getRowId,
  bulkBar,
}: DataTableProps<TData, TValue>): JSX.Element {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [globalFilterInput, setGlobalFilterInput] = useState('');
  const [density, setDensity] = useState<Density>('normal');
  const debouncedFilter = useDebouncedValue(globalFilterInput, 250);

  const allColumns = useMemo<ColumnDef<TData, TValue>[]>(() => {
    if (!enableSelection) return columns;
    const selectionColumn: ColumnDef<TData, TValue> = {
      id: '__select',
      size: 40,
      header: ({ table }) => (
        <input
          type="checkbox"
          aria-label="Tout sélectionner"
          checked={table.getIsAllPageRowsSelected()}
          ref={(el) => {
            if (el) el.indeterminate = table.getIsSomePageRowsSelected();
          }}
          onChange={(e) => table.toggleAllPageRowsSelected(!!e.target.checked)}
          className="h-4 w-4 rounded border-border accent-primary"
        />
      ),
      cell: ({ row }) => (
        <input
          type="checkbox"
          aria-label="Sélectionner la ligne"
          checked={row.getIsSelected()}
          onChange={(e) => row.toggleSelected(!!e.target.checked)}
          className="h-4 w-4 rounded border-border accent-primary"
        />
      ),
      enableSorting: false,
    };
    return [selectionColumn, ...columns];
  }, [columns, enableSelection]);

  const table = useReactTable({
    data,
    columns: allColumns,
    state: { sorting, rowSelection, columnVisibility, globalFilter: debouncedFilter },
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
    onColumnVisibilityChange: setColumnVisibility,
    onGlobalFilterChange: setGlobalFilterInput,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: enableSelection,
    initialState: { pagination: { pageSize } },
    ...(getRowId ? { getRowId } : {}),
  });

  useEffect(() => {
    table.setGlobalFilter(debouncedFilter);
  }, [debouncedFilter, table]);

  // Notify parent when selection changes
  useEffect(() => {
    if (!onSelectionChange) return;
    const selectedRows = table.getSelectedRowModel().rows.map((r) => r.original);
    onSelectionChange(selectedRows);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowSelection]);

  const cellPad = DENSITY_PADDING[density];
  const totalRows = table.getFilteredRowModel().rows.length;
  const selectedCount = Object.values(rowSelection).filter(Boolean).length;

  const handleExportCSV = (): void => {
    const headers = table
      .getVisibleLeafColumns()
      .filter((c) => c.id !== '__select')
      .map((c) => String(c.columnDef.header ?? c.id));
    const rows = table.getFilteredRowModel().rows.map((row) =>
      row
        .getVisibleCells()
        .filter((c) => c.column.id !== '__select')
        .map((cell) => csvEscape(cell.getValue())),
    );
    const csv = [headers.map(csvEscape).join(','), ...rows.map((r) => r.join(','))].join('\n');
    downloadFile(csv, exportFileName, 'text/csv;charset=utf-8;');
  };

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      {bulkBar}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={globalFilterInput}
            onChange={(e) => setGlobalFilterInput(e.target.value)}
            placeholder={searchPlaceholder ?? t('common.search')}
            className="h-9 w-72 pl-8"
          />
        </div>
        <div className="ml-auto flex items-center gap-2">
          {selectedCount > 0 && (
            <span className="text-xs text-muted-foreground">
              {selectedCount} {t('common.selected')}
            </span>
          )}
          <DensityToggle value={density} onChange={setDensity} />
          <Button variant="outline" size="sm" onClick={handleExportCSV}>
            <Download className="h-4 w-4" />
            {t('common.export')}
          </Button>
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border border-border bg-card">
        <div className="scrollbar-thin overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              {table.getHeaderGroups().map((group) => (
                <tr key={group.id} className="border-b border-border bg-muted/40">
                  {group.headers.map((header) => {
                    const sorted = header.column.getIsSorted();
                    const sortable = header.column.getCanSort();
                    return (
                      <th
                        key={header.id}
                        className={cn(
                          'text-left font-semibold text-muted-foreground',
                          'whitespace-nowrap',
                          cellPad,
                        )}
                        style={{ width: header.getSize() === 0 ? undefined : header.getSize() }}
                      >
                        {header.isPlaceholder ? null : (
                          <button
                            type="button"
                            className={cn(
                              'flex items-center gap-1.5 text-left',
                              sortable && 'cursor-pointer select-none hover:text-foreground',
                              !sortable && 'cursor-default',
                            )}
                            onClick={sortable ? header.column.getToggleSortingHandler() : undefined}
                          >
                            {flexRender(header.column.columnDef.header, header.getContext())}
                            {sortable && (
                              <span className="text-muted-foreground/60">
                                {sorted === 'asc' ? (
                                  <ArrowUp className="h-3 w-3" />
                                ) : sorted === 'desc' ? (
                                  <ArrowDown className="h-3 w-3" />
                                ) : (
                                  <ChevronsUpDown className="h-3 w-3" />
                                )}
                              </span>
                            )}
                          </button>
                        )}
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: pageSize }).map((_, i) => (
                  <tr key={`sk-${i}`} className="border-b border-border last:border-0">
                    {allColumns.map((_col, j) => (
                      <td key={`sk-${i}-${j}`} className={cellPad}>
                        <div className="h-3 w-3/4 animate-pulse rounded bg-muted" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : table.getRowModel().rows.length === 0 ? (
                <tr>
                  <td colSpan={allColumns.length} className="py-12">
                    <div className="flex flex-col items-center gap-2 text-muted-foreground">
                      <Inbox className="h-8 w-8" />
                      <span className="text-sm">{emptyMessage ?? t('common.noResults')}</span>
                    </div>
                  </td>
                </tr>
              ) : (
                table.getRowModel().rows.map((row, idx) => (
                  <tr
                    key={row.id}
                    className={cn(
                      'border-b border-border transition-colors last:border-0 hover:bg-muted/30',
                      idx % 2 === 1 && 'bg-muted/10',
                      row.getIsSelected() && 'bg-primary/5',
                    )}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} className={cn(cellPad, 'align-middle')}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
        <div>
          {totalRows} ligne{totalRows > 1 ? 's' : ''}{' '}
          {selectedCount > 0 && `· ${selectedCount} ${t('common.selected')}`}
        </div>
        <div className="flex items-center gap-2">
          <select
            value={table.getState().pagination.pageSize}
            onChange={(e) => table.setPageSize(Number(e.target.value))}
            className="h-8 rounded-md border border-input bg-background px-2 text-xs"
            aria-label={t('common.rowsPerPage')}
          >
            {[10, 20, 50, 100].map((s) => (
              <option key={s} value={s}>
                {s} / page
              </option>
            ))}
          </select>
          <span>
            {t('common.of')} {table.getPageCount() || 1}
          </span>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => table.setPageIndex(0)}
              disabled={!table.getCanPreviousPage()}
              aria-label={t('common.first')}
            >
              <ChevronsLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              aria-label={t('common.previous')}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="px-1 font-mono">{table.getState().pagination.pageIndex + 1}</span>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
              aria-label={t('common.next')}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => table.setPageIndex(table.getPageCount() - 1)}
              disabled={!table.getCanNextPage()}
              aria-label={t('common.last')}
            >
              <ChevronsRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

interface DensityToggleProps {
  value: Density;
  onChange: (density: Density) => void;
}

function DensityToggle({ value, onChange }: DensityToggleProps): JSX.Element {
  const options: Density[] = ['compact', 'normal', 'comfortable'];
  const labels: Record<Density, string> = {
    compact: 'Compact',
    normal: 'Normal',
    comfortable: 'Confort',
  };
  return (
    <div className="inline-flex h-8 rounded-md border border-input bg-background p-0.5 text-xs">
      {options.map((opt) => (
        <button
          key={opt}
          type="button"
          onClick={() => onChange(opt)}
          className={cn(
            'rounded-sm px-2 transition-colors',
            value === opt
              ? 'bg-secondary text-foreground'
              : 'text-muted-foreground hover:text-foreground',
          )}
        >
          {labels[opt]}
        </button>
      ))}
    </div>
  );
}

function csvEscape(value: unknown): string {
  if (value === null || value === undefined) return '';
  const str = typeof value === 'string' ? value : JSON.stringify(value);
  if (/[",\n;]/.test(str)) return `"${str.replace(/"/g, '""')}"`;
  return str;
}

function downloadFile(content: string, filename: string, mime: string): void {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
